/**
 * Every moment lands in the town (v1 ticket 41), without the DOM: which words fall, which lines show, the town
 * before a district rises, where on the ground it rises, and which of the first lesson's cards hang as signs.
 *
 * - A new word is a building homed in the landing district and still in the fog, the items the climb's gate holds
 *   included (they are fog lots). Glue words, transcript lines and unkept seeds are never buildings, so they never
 *   fall; a word heard before in another moment is homed there, so it does not fall either: its road comes with the
 *   district.
 * - Up to `LANDING_LINES` of the moment's lines show (from the blueprints route, so the words that fall are the
 *   ones the moment screen marks), the ones holding new words first, in the order they were said.
 * - The district rises where the town after the landing puts it. Earlier islands never move when a later moment
 *   arrives (layout.ts), so the ground it rises on is its island, the lagoon new around it and its bridges' ends;
 *   only a landing out of order (an older moment still waiting) moves islands, and then the whole picture fades.
 * - A lesson card hangs as a sign under its lot while its word is in the fog on screen, until its fog lifted in
 *   this lesson; any other card covers the town.
 * - Home opens the oldest moment waiting to land (`landing` on the town), each once per visit.
 *
 * Materials only: a box never shows.
 */
import type { Cell } from './camera';
import { lineFloorMs } from '../scene-lines';
import { cid, type Layout } from './layout';
import type { OwnTown } from './own-town';
import type { BlueprintPart, MomentBlueprints, PackTask } from '../types';

/** Lines shown over the town while a moment lands. */
export const LANDING_LINES = 4;

/** The words that fall into a landing district: its own buildings still in the fog, in the town's order. */
export function fallingWords(own: OwnTown, momentId: string): string[] {
  return own.buildings.filter((b) => b.district === momentId && b.material === 'fog').map((b) => b.key);
}

/** A line shown while a moment lands. `falls`: the falling words that light up in it, each in one line only. */
export interface LandingLine {
  /** Its place in the transcript. */
  index: number;
  speaker: string;
  en: string;
  parts: BlueprintPart[];
  /** Finglish, for a Persian line: shown first. */
  latinParts?: BlueprintPart[];
  falls: string[];
}

/** The falling words a line holds, in the order they are said (a pattern the line holds counts as held). */
function heldIn(line: MomentBlueprints['lines'][number], falling: ReadonlySet<string>): string[] {
  const keys = [...(line.latinParts ?? line.parts), ...line.parts].map((p) => p.key).filter((k): k is string => !!k);
  for (const p of line.patterns ?? []) keys.push(p.key);
  return [...new Set(keys.filter((k) => falling.has(k)))];
}

/**
 * Up to `max` lines of the moment: the ones holding falling words first, then the rest, shown in the order they
 * were said. Each falling word lights up in the first shown line that holds it.
 */
export function shownLines(bp: MomentBlueprints, falling: readonly string[], max = LANDING_LINES): LandingLine[] {
  const want = new Set(falling);
  const all = bp.lines.map((line, index) => ({ line, index, held: heldIn(line, want) }));
  const chosen = [...all.filter((x) => x.held.length), ...all.filter((x) => !x.held.length)].slice(0, Math.max(0, max));
  chosen.sort((a, b) => a.index - b.index);
  const lit = new Set<string>();
  return chosen.map(({ line, index, held }) => {
    const falls = held.filter((k) => !lit.has(k));
    for (const k of falls) lit.add(k);
    return {
      index, speaker: line.speaker, en: line.en, parts: line.parts, falls,
      ...(line.latinParts ? { latinParts: line.latinParts } : {}),
    };
  });
}

/**
 * An imagined scene's lines for its landing (heard, then caught): every line, in the order said, each falling word
 * lit in the first line that holds it.
 */
export function sceneLines(bp: MomentBlueprints, falling: readonly string[]): LandingLine[] {
  return shownLines(bp, falling, Infinity);
}

/** A line's target text, as the scene's clip says it (the script, never the Finglish). */
export function lineText(l: LandingLine): string {
  return l.parts.map((p) => p.text).join('');
}

/** The other person's name or role in a scene: the first speaker who isn't the learner, '' when none is named. */
export function sceneOther(lines: readonly LandingLine[], isYou: (speaker: string) => boolean): string {
  return lines.find((l) => l.speaker.trim() && !isYou(l.speaker))?.speaker.trim() ?? '';
}

/** Least time a scene line is given before the landing moves on without it, and time per character above that. */
export const SCENE_LINE_MIN_MS = 2500;
export const SCENE_MS_PER_CHAR = 70;
/** Slack on top of the lines' time, once the scene is heard to play. */
export const SCENE_SLACK_MS = 3000;

/** A line's share of the cap: max(2.5 s, 70 ms a character), and never less than its reading-pace floor. */
export function sceneLineCapMs(text: string): number {
  return Math.max(lineFloorMs(text), SCENE_LINE_MIN_MS, SCENE_MS_PER_CHAR * [...text].length);
}

/**
 * How long a scene's voice may play before the landing stops it regardless (a clip or voice that never ends): each
 * line's share (`sceneLineCapMs`), summed, plus slack. The cap stops the voice only: the lines it reached still get
 * their reading-pace floor (`scenePacer`), and the cap is never below the floors' sum, so a pass waiting on the
 * floor alone is never cut.
 */
export function sceneCapMs(lines: readonly LandingLine[]): number {
  return lines.reduce((ms, l) => ms + sceneLineCapMs(lineText(l)), 0) + SCENE_SLACK_MS;
}

/** The order the words fall in: line by line as shown, then the words no shown line holds (`line` null). */
export function fallOrder(lines: readonly LandingLine[], falling: readonly string[]): { key: string; line: number | null }[] {
  const out = lines.flatMap((l) => l.falls.map((key) => ({ key, line: l.index as number | null })));
  const seen = new Set(out.map((x) => x.key));
  for (const key of falling) if (!seen.has(key)) out.push({ key, line: null });
  return out;
}

/**
 * The town without some districts: their buildings, their place in other words' roads and the roads that touch
 * them gone. `withoutDistricts(own, waiting)` is the town before the landing; without the others still waiting,
 * the town once this one has landed.
 */
export function withoutDistricts<T extends OwnTown>(own: T, ids: ReadonlySet<string>): T {
  if (!ids.size) return own;
  return {
    ...own,
    districts: own.districts.filter((d) => !ids.has(d.id)),
    buildings: own.buildings
      .filter((b) => !ids.has(b.district))
      .map((b) => (b.also.some((x) => ids.has(x)) ? { ...b, also: b.also.filter((x) => !ids.has(x)) } : b)),
    roads: own.roads.filter((r) => !ids.has(r.a) && !ids.has(r.b)),
  };
}

/**
 * Where a landing district rises: its island, the lagoon new around it and the ends of its bridges and garlands,
 * as cells of the town after the landing. `moved`: an earlier island stands elsewhere after it (a landing out of
 * order), so the whole picture must change, not only this ground.
 */
export function riseCells(before: Layout | null, after: Layout, id: string): { cells: Cell[]; moved: boolean } {
  const block = after.byDistrict.get(id);
  if (!block) return { cells: [], moved: false };
  const had = new Set<number>();
  for (const b of before?.blocks ?? []) for (const [x, y] of b.cells) had.add(cid(x, y));
  for (const [x, y] of before?.water ?? []) had.add(cid(x, y));
  const cells: Cell[] = block.cells.map(([x, y]) => [x, y]);
  for (const [x, y] of after.water) if (!had.has(cid(x, y))) cells.push([x, y]);
  for (const br of after.bridges) {
    if (br.a !== block && br.b !== block) continue;
    for (const s of [br.from, br.to]) cells.push(br.axis === 0 ? [s, br.row] : [br.row, s]);
  }
  const middle = (c: readonly [number, number][]): Cell => c[Math.floor(c.length / 2)] ?? [0, 0];
  for (const g of after.garlands) {
    if (g.a !== block && g.b !== block) continue;
    if (g.from) cells.push([g.from.gx, g.from.gy]);
    cells.push(middle(g.a.cells), middle(g.b.cells));
  }
  // The empty land the first district took (v1 ticket 40), and the props where they stood and where they stand now,
  // so the rebuild's reveal covers them all; none of them moves a district, so `moved` stays as it was.
  for (const [x, y] of before?.plot?.cells ?? []) cells.push([x, y]);
  if (before?.props) cells.push([...before.props.cell]);
  if (after.props) cells.push([...after.props.cell]);
  const moved = (before?.blocks ?? []).some((b) => {
    const now = after.byDistrict.get(b.d.id);
    return !!now && (now.x !== b.x || now.y !== b.y);
  });
  return { cells, moved };
}

/**
 * The ghost of a coming district as SVG paths through `project` (ground cell corners to screen px): `fill` one
 * diamond per cell, filled together as one patch; `edge` only the sides no other of its cells shares, its outline.
 */
export function ghostPaths(cells: readonly Cell[], project: (x: number, y: number) => readonly [number, number]): { fill: string; edge: string } {
  const has = new Set(cells.map(([x, y]) => cid(x, y)));
  const p = (x: number, y: number) => { const [sx, sy] = project(x, y); return `${sx.toFixed(1)} ${sy.toFixed(1)}`; };
  const seg = (a: Cell, b: Cell) => `M${p(a[0], a[1])}L${p(b[0], b[1])}`;
  let fill = '', edge = '';
  for (const [x, y] of cells) {
    fill += `M${p(x, y)}L${p(x + 1, y)}L${p(x + 1, y + 1)}L${p(x, y + 1)}Z`;
    if (!has.has(cid(x, y - 1))) edge += seg([x, y], [x + 1, y]);
    if (!has.has(cid(x + 1, y))) edge += seg([x + 1, y], [x + 1, y + 1]);
    if (!has.has(cid(x, y + 1))) edge += seg([x + 1, y + 1], [x, y + 1]);
    if (!has.has(cid(x - 1, y))) edge += seg([x, y + 1], [x, y]);
  }
  return { fill, edge };
}

/**
 * Does a lesson card hang as a sign under its word's lot? Only while the word is in the fog in the town on screen
 * and its fog has not lifted in this lesson; a word the town has no building for covers the town like any card.
 * A flashcard never hangs: a flip is saved unscored, so it cannot lift the fog.
 */
export function hangsAsSign(key: string, type: string, shown: OwnTown | null | undefined, lifted: ReadonlySet<string>): boolean {
  if (type === 'flashcard' || lifted.has(key)) return false;
  return shown?.buildings.find((b) => b.key === key)?.material === 'fog';
}

/**
 * Landings home has opened in this visit. A landing acks once its district has risen; one whose ack never got
 * through (offline, a page left mid-way) is not opened again until the page reloads, so home never loops.
 */
const opened = new Set<string>();

/** The oldest moment waiting to land that home has not opened yet in this visit, if any. */
export function nextLanding(own: OwnTown): string | null {
  const here = new Set(own.districts.map((d) => d.id));
  return (own.landing ?? []).find((id) => here.has(id) && !opened.has(id)) ?? null;
}

/** Home is opening this landing (or the learner skipped it): not again in this visit. */
export function markOpened(...ids: string[]): void {
  for (const id of ids) opened.add(id);
}

/** The first lesson's cards: the day's cut as served, less the cards answered today and the words removed. */
export function lessonTasks(tasks: readonly PackTask[], answeredToday: readonly string[], removed: ReadonlySet<string>): PackTask[] {
  const done = new Set(answeredToday);
  return tasks.filter((t) => !done.has(t.id) && !removed.has(t.itemKey));
}
